import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import { apiClient, InitData } from '../../core'
import { t, intlLocale, dayNames, formatMoney } from '../../core/i18n'
import { formatDuration } from '../../core/duration'
import { dagNoegle, fmtDato, fmtTid, studietsTidszone, ugedagForNoegle } from '../../core/tidszone'

interface KlinikService {
  id: string
  name: string
}

interface KlinikSlot {
  startTime: string
  endTime: string
  practitioner: { name: string }
  tenantName: string
  templateTitle: string
  priceDkk: number
  /** Gruppebooking (OPG-0041): prisen er pr. person. */
  pricePerPerson?: boolean
  durationMinutes: number
  bookUrl: string
}

interface KlinikAvailabilityResponse {
  services: KlinikService[]
  slots: KlinikSlot[]
}

interface Props {
  initData: InitData
  title?: string | null
}

const INITIAL_DAYS = 7
const DAYS_STEP = 7
/** Matches the API's clamp — asking for more would be silently truncated. */
const MAX_DAYS = 30

// Dage, datoer og tider følger husets tidszone (initData.timezone), ikke UTC og ikke den
// besøgendes browser (core/tidszone.ts). Før blev tiderne grupperet på
// `slot.startTime.slice(0, 10)` (UTC-datoen), så en behandling kl. 00:00-01:59 dansk tid
// stod under dagen før.

/**
 * KlinikScheduleWidget — the house's combined "first available" treatment
 * times across its member clinics (lejere), embedded on the house's OWN
 * external website with the group OWNER's api-key.
 *
 * Layout mirrors ScheduleWidget: a service picker (chips, "all" default),
 * day-grouped slot buttons (time, treatment, practitioner, clinic, price)
 * and a "load more days" button that widens the window up to the API's cap.
 *
 * Clicking a slot deliberately LEAVES the site: booking and payment MUST
 * happen in the member clinic's own flow, under the clinic's own CVR — the
 * clinic is the contracting party and payment recipient, not the house.
 * This widget is a picker, not a checkout, so bookUrl opens in a new tab.
 */
export function KlinikScheduleWidget({ initData, title = null }: Props) {
  const str = t(initData.locale)
  const dateLocale = intlLocale(initData.locale)
  const tz = studietsTidszone(initData.timezone)
  const DAY_LABELS = dayNames(initData.locale)

  const [services, setServices] = useState<KlinikService[]>([])
  const [slots, setSlots] = useState<KlinikSlot[]>([])
  const [serviceId, setServiceId] = useState<string | null>(null)
  const [days, setDays] = useState(INITIAL_DAYS)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Set when the refetch was triggered by "load more days", so the existing
  // list stays on screen (button spinner) instead of collapsing to a skeleton.
  const moreRequested = useRef(false)

  const fetchData = useCallback(async () => {
    const isMore = moreRequested.current
    moreRequested.current = false
    if (isMore) setLoadingMore(true)
    else setLoading(true)
    setError(null)

    const qs = new URLSearchParams({ days: String(days), limit: '100' })
    if (serviceId) qs.set('serviceId', serviceId)
    const result = await apiClient.get<KlinikAvailabilityResponse>(
      `/api/widget/klinik-availability?${qs.toString()}`
    )

    if (result.error) {
      setError(result.error)
    } else if (result.data) {
      setServices(result.data.services)
      setSlots(result.data.slots)
    }
    setLoading(false)
    setLoadingMore(false)
  }, [serviceId, days])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  function pickService(id: string | null) {
    if (id === serviceId) return
    setServiceId(id)
  }

  function showMoreDays() {
    moreRequested.current = true
    setDays(d => Math.min(MAX_DAYS, d + DAYS_STEP))
  }

  function openBooking(slot: KlinikSlot) {
    // Deliberate leave-site (see the component doc comment): the booking and
    // the payment belong in the member clinic's own flow under its own CVR.
    window.open(slot.bookUrl, '_blank', 'noopener')
  }

  // Group slots by date (yyyy-mm-dd from startTime), like ScheduleWidget.
  const grouped = new Map<string, KlinikSlot[]>()
  for (const slot of slots) {
    const key = dagNoegle(slot.startTime, tz)
    const list = grouped.get(key) || []
    list.push(slot)
    grouped.set(key, list)
  }
  const sortedDays = Array.from(grouped.keys()).sort()

  const showPicker = services.length > 1

  return (
    <div>
      <style>{styles}</style>
      {title && <h2 class="hsw-kli-title-main">{title}</h2>}

      {/* Service picker — chips with an "all services" default */}
      {showPicker && (
        <div class="hsw-kli-picker" role="group">
          <button
            type="button"
            class={`hsw-kli-chip${serviceId === null ? ' hsw-kli-chip-active' : ''}`}
            onClick={() => pickService(null)}
          >
            {str.klinikPickerAllServices}
          </button>
          {services.map(svc => (
            <button
              key={svc.id}
              type="button"
              class={`hsw-kli-chip${serviceId === svc.id ? ' hsw-kli-chip-active' : ''}`}
              onClick={() => pickService(svc.id)}
            >
              {svc.name}
            </button>
          ))}
        </div>
      )}

      {/* Loading skeleton — 2 day-groups with a label and slot rows */}
      {loading && [0, 1].map(i => (
        <div key={i} class="hsw-kli-day-group">
          <div class="hsw-kli-skel hsw-kli-skel-label" />
          <div class="hsw-kli-skel hsw-kli-skel-row" />
          <div class="hsw-kli-skel hsw-kli-skel-row" />
          <div class="hsw-kli-skel hsw-kli-skel-row" />
        </div>
      ))}

      {/* Error */}
      {!loading && error && (
        <div class="hsw-kli-error" role="alert">
          <strong>{str.loadError}</strong>
          <div class="hsw-kli-error-msg">{error}</div>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && slots.length === 0 && (
        <div class="hsw-kli-empty">
          <div class="hsw-kli-empty-icon" aria-hidden="true">🗓️</div>
          <p>{str.klinikNoSlots}</p>
        </div>
      )}

      {/* Day-grouped slots */}
      {!loading && !error && slots.length > 0 && sortedDays.map(dateKey => {
        const dayEntries = grouped.get(dateKey)!
        const day = ugedagForNoegle(dateKey)
        return (
          <div key={dateKey} class="hsw-kli-day-group">
            <div class="hsw-kli-day-label">
              {DAY_LABELS[day]} {fmtDato(dayEntries[0].startTime, dateLocale, tz)}
            </div>
            {dayEntries.map(slot => (
              <button
                key={`${slot.bookUrl}-${slot.startTime}`}
                type="button"
                class="hsw-kli-slot"
                title={str.klinikBookAt.replace('{name}', slot.tenantName)}
                aria-label={`${str.klinikBookAt.replace('{name}', slot.tenantName)} ${fmtTid(slot.startTime, dateLocale, tz)}`}
                onClick={() => openBooking(slot)}
              >
                <span class="hsw-kli-slot-time">{fmtTid(slot.startTime, dateLocale, tz)}</span>
                <span class="hsw-kli-slot-info">
                  <span class="hsw-kli-slot-name">{slot.templateTitle}</span>
                  <span class="hsw-kli-slot-meta">
                    {slot.practitioner.name} · {slot.tenantName} · {formatDuration(slot.durationMinutes, initData.locale)}
                  </span>
                </span>
                <span class="hsw-kli-slot-price">
                  {slot.pricePerPerson
                    ? str.pricePerPerson.replace('{price}', formatMoney(slot.priceDkk, initData.locale, initData.currency))
                    : formatMoney(slot.priceDkk, initData.locale, initData.currency)}
                </span>
              </button>
            ))}
          </div>
        )
      })}

      {/* Load more days */}
      {!loading && !error && days < MAX_DAYS && (
        <div class="hsw-kli-more-wrap">
          <button
            type="button"
            class="hsw-kli-more"
            onClick={showMoreDays}
            disabled={loadingMore}
          >
            {loadingMore ? <span class="hsw-kli-spinner" aria-label={str.klinikShowMore} /> : str.klinikShowMore}
          </button>
        </div>
      )}
    </div>
  )
}

const styles = `
  .hsw-kli-title-main {
    font-size: 2rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    text-align: center;
    margin: 0 auto 2rem;
    max-width: 1100px;
    letter-spacing: -0.01em;
  }

  /* Service picker */
  .hsw-kli-picker {
    max-width: 1100px;
    margin: 0 auto 1.75rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .hsw-kli-chip {
    padding: 0.5rem 1rem;
    border-radius: 999px;
    border: 1px solid #d4d4d4;
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    letter-spacing: 0.01em;
    transition: background .2s, color .2s, border-color .2s;
  }
  .hsw-kli-chip:hover {
    border-color: var(--yoga-primary, #FF6B35);
    color: var(--yoga-primary, #FF6B35);
  }
  .hsw-kli-chip-active,
  .hsw-kli-chip-active:hover {
    background: var(--yoga-primary, #FF6B35);
    border-color: var(--yoga-primary, #FF6B35);
    color: #fff;
  }

  /* Day groups */
  .hsw-kli-day-group {
    margin-bottom: 2.5rem;
    max-width: 1100px;
    margin-left: auto;
    margin-right: auto;
  }
  .hsw-kli-day-label {
    font-size: 0.85rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--yoga-primary, #FF6B35);
    padding-bottom: 0.75rem;
    border-bottom: 1px solid var(--yoga-primary, #FF6B35);
    margin-bottom: 1rem;
  }

  /* Slot rows (buttons) */
  .hsw-kli-slot {
    display: grid;
    grid-template-columns: 90px 1fr auto;
    gap: 1.25rem;
    align-items: center;
    width: 100%;
    text-align: left;
    padding: 1rem 1.25rem;
    background: var(--yoga-card-bg, #fff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    margin-bottom: 0.5rem;
    cursor: pointer;
    font-family: inherit;
    transition: border-color .3s, box-shadow .3s;
  }
  .hsw-kli-slot:hover {
    border-color: var(--yoga-primary, #FF6B35);
    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
  }
  .hsw-kli-slot-time {
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    font-size: 0.95rem;
    letter-spacing: -0.01em;
  }
  .hsw-kli-slot-info {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
  }
  .hsw-kli-slot-name {
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    font-size: 1rem;
    letter-spacing: -0.01em;
  }
  .hsw-kli-slot-meta {
    color: var(--yoga-text-muted, #737373);
    font-size: 0.85rem;
  }
  .hsw-kli-slot-price {
    font-size: 0.9rem;
    font-weight: 700;
    color: var(--yoga-primary, #FF6B35);
    white-space: nowrap;
  }

  /* Load more */
  .hsw-kli-more-wrap {
    max-width: 1100px;
    margin: 0 auto;
    text-align: center;
  }
  .hsw-kli-more {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0.7rem 1.5rem;
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    font-weight: 500;
    border: 1px solid #d4d4d4;
    border-radius: var(--yoga-radius, 0);
    font-size: 0.85rem;
    letter-spacing: 0.01em;
    cursor: pointer;
    font-family: inherit;
    transition: all .3s;
  }
  .hsw-kli-more:hover:not(:disabled) {
    background: var(--yoga-text, #0a0a0a);
    color: var(--yoga-card-bg, #fff);
    border-color: var(--yoga-text, #0a0a0a);
  }
  .hsw-kli-more:disabled { opacity: 0.6; cursor: not-allowed; }
  .hsw-kli-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(0,0,0,0.2);
    border-top-color: var(--yoga-text, #0a0a0a);
    border-radius: 50%;
    display: inline-block;
    animation: hsw-kli-spin .8s linear infinite;
  }

  /* Empty */
  .hsw-kli-empty {
    padding: 3rem;
    text-align: center;
    color: #737373;
  }
  .hsw-kli-empty-icon {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }

  /* Error */
  .hsw-kli-error {
    max-width: 1100px;
    margin: 0 auto;
    padding: 1rem 1.25rem;
    border: 1px solid #fecaca;
    background: #fef2f2;
    color: #991b1b;
    border-radius: var(--yoga-radius, 0);
    font-size: 0.9rem;
    line-height: 1.5;
  }
  .hsw-kli-error-msg {
    margin-top: 0.25rem;
    color: #b91c1c;
    font-size: 0.85rem;
  }

  /* Skeletons */
  .hsw-kli-skel {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-kli-shimmer 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 0.5rem;
  }
  .hsw-kli-skel-label { height: 14px; width: 30%; margin-bottom: 1rem; }
  .hsw-kli-skel-row { height: 60px; width: 100%; }

  @keyframes hsw-kli-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  @keyframes hsw-kli-spin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 600px) {
    .hsw-kli-slot {
      grid-template-columns: 70px 1fr;
      gap: 0.5rem 0.75rem;
      padding: 0.85rem 1rem;
    }
    .hsw-kli-slot-price {
      grid-column: 1 / -1;
      padding-top: 0.4rem;
      border-top: 1px solid #f0f0f0;
    }
  }
`
